import { useQuery } from '@apollo/client';
import { Button, Card, Col, Row, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Checks, DownloadSimple, FilePDF } from '../../../assets/svg';
import {
  defaultDateFormat,
  GUTTER_VARIATIONS,
  NA_PLACEHOLDER_TEXT,
} from '../../../common/constants';
import { LABEL_TEXT } from '../../../common/labelTexts';
import LoaderComponent from '../../../components/LoaderComponent';
import CommonModal from '../../../components/modals/CommonModal';
import MyBreadcrumb from '../../../components/MyBreadcrumb';
import useRouter from '../../../hooks/useRouter';
import { ITF } from './graphql/Queries';
const { Text, Title } = Typography;

const OtherFiledResponse = () => {
  const { navigate, params } = useRouter();
  const [searchParams] = useSearchParams();
  const { resId } = params;
  const [openModal, setOpenModal] = useState(false);
  const client = searchParams.get('client');

  const { data: { itf } = {}, loading: loadResponse } = useQuery(ITF, {
    fetchPolicy: 'network-only',
    onError() {},
    variables: { where: { id: resId } },
  });

  const cancel = () => {
    setOpenModal(false);
  };

  const handleAttachments = (e: React.MouseEvent) => {
    if (itf?.attachments && itf.attachments.length > 1) {
      e.preventDefault();
      setOpenModal(true);
    }
  };

  return (
    <div className="container">
      <div className="mt-16 mb-16 d-flex justify-between gap-32">
        <MyBreadcrumb
          username={itf?.assessee?.username ?? (client || '')}
          id={itf?.assessee?._id ?? (client || '')}
        />
        <Text className="title">
          {itf?.assessee?.username} | {itf?.assessee?.name}
        </Text>
      </div>
      <div className="d-flex justify-between align-center">
        <Text className="heading">{LABEL_TEXT.OTHER_FILED_FORM}</Text>
      </div>
      <LoaderComponent spinning={loadResponse}>
        <Card className="mt-16">
          <Title level={5} className="mb-24 mt-0">
            {itf?.name || NA_PLACEHOLDER_TEXT}
          </Title>
          <div className="mb-40 mt-40">
            <Row
              gutter={[GUTTER_VARIATIONS, GUTTER_VARIATIONS]}
              className="mb-16"
            >
              <Col xs={12} lg={6} xl={6} className="d-grid">
                <Text className="title">{LABEL_TEXT.FORM_NUMBER}</Text>
                <Text className="value">
                  {itf?.formNo || NA_PLACEHOLDER_TEXT}
                </Text>
              </Col>
              <Col xs={12} lg={6} xl={6} className="d-grid">
                <Text className="title">{LABEL_TEXT.FULL_ASSESSMENT_YEAR}</Text>
                <Text className="value">
                  {itf?.assessmentYear || NA_PLACEHOLDER_TEXT}
                </Text>
              </Col>
              <Col xs={12} lg={6} xl={6} className="d-grid">
                <Text className="title">{LABEL_TEXT.FILING_DATE}</Text>
                <Text className="value">
                  {itf?.filingDate
                    ? dayjs(itf?.filingDate).format(defaultDateFormat)
                    : NA_PLACEHOLDER_TEXT}
                </Text>
              </Col>
              <Col xs={12} lg={6} xl={6} className="d-grid">
                <Text className="title">{LABEL_TEXT.FILING_TYPE}</Text>
                <Text className="value">
                  {itf?.filingType || NA_PLACEHOLDER_TEXT}
                </Text>
              </Col>
            </Row>
            <Row
              gutter={[GUTTER_VARIATIONS, GUTTER_VARIATIONS]}
              className="mb-16"
            >
              <Col xs={12} lg={6} xl={6} className="d-grid">
                <Text className="title">{LABEL_TEXT.STATUS}</Text>
                <Text>
                  <Tag color="blue" title="verified">
                    {itf?.status || NA_PLACEHOLDER_TEXT}
                  </Tag>
                </Text>
              </Col>
              <Col xs={12} lg={6} xl={6} className="d-grid">
                <Text className="title">{LABEL_TEXT.ACKNOWLEDGEMENT_NO}</Text>
                <Text className="value">
                  {itf?.acknowledgmentNo || NA_PLACEHOLDER_TEXT}
                </Text>
              </Col>
              <Col xs={12} lg={6} xl={6} className="d-grid">
                <Text className="title">{LABEL_TEXT.FILED_BY}</Text>
                <Text className="value">
                  {itf?.filedBy || NA_PLACEHOLDER_TEXT}
                </Text>
              </Col>
              <Col xs={12} lg={6} xl={6} className="d-grid">
                <Text className="title">{LABEL_TEXT.FORM_SUBMITTED}</Text>
                <Text className="value">
                  {itf?.formSubmittedDate
                    ? dayjs(itf?.formSubmittedDate).format(defaultDateFormat)
                    : NA_PLACEHOLDER_TEXT}
                </Text>
              </Col>
            </Row>
          </div>
          <Row gutter={[24, 24]} className="mb-8" key="1">
            <Col xs={24} lg={24} xl={24}>
              <Card>
                <div className="submission-card d-flex justify-between gap-16">
                  <div className="d-flex align-center">
                    <span className="check-icon">
                      <Checks />
                    </span>
                    <div className="d-grid detail">
                      <Text>Activity / Status</Text>
                      <Text className="title">
                        To be displayed for AY {itf?.assessmentYear} onwards
                      </Text>
                    </div>
                  </div>
                  <div className="d-grid files-detail">
                    <Link
                      className="color-blue multi-pdf"
                      to={itf?.forms?.[0]?.url ?? ''}
                      download
                      target="_blank"
                    >
                      <Button
                        title={
                          !itf?.forms?.length
                            ? 'No forms available to download'
                            : ''
                        }
                        type="link"
                        className="d-flex align-center "
                        icon={<DownloadSimple />}
                        disabled={!itf?.forms?.length}
                      >
                        <span className="fs-12">
                          {LABEL_TEXT.DOWNLOAD_FORM}
                        </span>
                      </Button>
                    </Link>

                    <Link
                      className="color-blue multi-pdf"
                      to={itf?.receipts?.[0]?.url ?? ''}
                      download
                      target="_blank"
                    >
                      <Button
                        title={
                          !itf?.receipts?.length
                            ? 'No receipts available to download'
                            : ''
                        }
                        type="link"
                        className="d-flex align-center"
                        icon={<DownloadSimple />}
                        disabled={!itf?.receipts?.length}
                      >
                        {LABEL_TEXT.DOWNLOAD_RECEIPT}
                      </Button>
                    </Link>
                    <Link
                      className="color-blue multi-pdf"
                      to={itf?.attachments?.[0]?.url ?? ''}
                      download
                      target="_blank"
                      onClick={handleAttachments}
                    >
                      <Button
                        title={
                          !itf?.attachments?.length
                            ? 'No attachment available to download'
                            : ''
                        }
                        type="link"
                        className="d-flex align-center"
                        icon={<DownloadSimple />}
                        disabled={!itf?.attachments?.length}
                      >
                        {LABEL_TEXT.DOWNLOAD_ATTACHMENT}
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>

          <Button
            type="default"
            className="float-right"
            onClick={() => navigate(-1)}
          >
            {LABEL_TEXT.BACK_TO_LIST}
          </Button>
        </Card>
      </LoaderComponent>
      {openModal && (
        <div className="attachments">
          <CommonModal
            className="h-512"
            title="Attachments"
            open={openModal}
            footer={false}
            closable={true}
            onCancel={cancel}
            wrapClassName="attachment-modal"
          >
            <div className="d-flex gap-16 flex-vertical">
              {itf?.attachments?.map((file, index: number) => (
                <div className="d-flex align-center" key={index}>
                  <span className="mr-8 d-flex">
                    <FilePDF />
                  </span>
                  <Link
                    className="color-blue max-width-430"
                    to={file?.url ?? ''}
                    download={file?.url}
                    target="_blank"
                  >
                    {file?.url?.split('/').slice(-2).join('/').split('?')[0] ??
                      ''}
                  </Link>
                </div>
              ))}
            </div>
          </CommonModal>
        </div>
      )}
    </div>
  );
};

export default OtherFiledResponse;
